// src/utils/auth.ts
import { UserRole, AuthUser } from '@/types/models';
import { getImpersonation, stopImpersonation } from './impersonation';

/**
 * Obtiene el token del localStorage
 */
export const getToken = (): string | null => {
  return localStorage.getItem('token');
};

/**
 * Guarda el token en localStorage
 */
export const setToken = (token: string): void => {
  localStorage.setItem('token', token);
};

/**
 * Elimina el token del localStorage
 */
export const removeToken = (): void => {
  localStorage.removeItem('token');
};

/**
 * Verifica si el usuario está autenticado
 */
export const isAuthenticated = (): boolean => {
  const token = getToken();
  if (!token) return false;
  
  // Verificar si el token no ha expirado
  const expiration = localStorage.getItem('token_expiration');
  if (expiration) {
    const expirationTime = parseInt(expiration, 10);
    if (Date.now() > expirationTime) {
      // Token expirado, limpiar
      clearAuthData();
      return false;
    }
  }
  
  return true;
};

/**
 * Obtiene el rol actual del usuario. Mientras se está suplantando identidad
 * (ver utils/impersonation.ts) devuelve el rol del USUARIO SUPLANTADO — todas
 * las decisiones basadas en rol (menús, guards, permisos) deben seguir el
 * perfil suplantado, igual que hace el API en cada petición. El rol real
 * permanece intacto en localStorage y vuelve en cuanto termina la suplantación.
 */
export const getCurrentRole = (): UserRole | null => {
  const impersonation = getImpersonation();
  if (impersonation) return impersonation.role;
  const role = localStorage.getItem('role');
  return role as UserRole | null;
};

/**
 * Obtiene el ID del usuario actual
 */
export const getCurrentUserId = (): string | null => {
  return localStorage.getItem('userId');
};

/**
 * Obtiene el nombre del usuario actual
 */
export const getCurrentUserName = (): string | null => {
  return localStorage.getItem('username');
};

/**
 * Obtiene el email del usuario actual
 */
export const getCurrentUserEmail = (): string | null => {
  return localStorage.getItem('email');
};

/**
 * Obtiene el hotel actual del usuario (legacy - single hotel)
 */
export const getCurrentHotel = (): string | null => {
  return localStorage.getItem('hotelId');
};

/**
 * Obtiene el grupo actual del usuario (legacy - single group)
 */
export const getCurrentGroup = (): string | null => {
  return localStorage.getItem('groupId');
};

/**
 * Obtiene los grupos asignados al usuario (array)
 */
export const getAssignedGroups = (): string[] => {
  const groups = localStorage.getItem('assignedGroups');
  if (!groups) return [];
  try {
    return JSON.parse(groups);
  } catch {
    return [];
  }
};

/**
 * Obtiene los hoteles asignados al usuario (array)
 */
export const getAssignedHotels = (): string[] => {
  const hotels = localStorage.getItem('assignedHotels');
  if (!hotels) return [];
  try {
    return JSON.parse(hotels);
  } catch {
    return [];
  }
};

/**
 * Guarda los datos de autenticación
 */
export const saveAuthData = (data: {
  token: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: UserRole;
    hotelId?: string;
    groupId?: string;
    assignedGroups?: string[];
    assignedHotels?: string[];
    capabilities?: string[];
  };
  expiresIn?: number;
}): void => {
  const { token, user, expiresIn } = data;

  // Guardar token
  setToken(token);

  // Guardar datos del usuario
  localStorage.setItem('userId', user.id);
  localStorage.setItem('username', user.name);
  localStorage.setItem('email', user.email);
  localStorage.setItem('role', user.role);

  // Legacy single hotel/group (for backwards compatibility)
  if (user.hotelId) {
    localStorage.setItem('hotelId', user.hotelId);
  }

  if (user.groupId) {
    localStorage.setItem('groupId', user.groupId);
  }

  // New array-based assignments
  if (user.assignedGroups && user.assignedGroups.length > 0) {
    localStorage.setItem('assignedGroups', JSON.stringify(user.assignedGroups));
    // Also set groupId to first group for backwards compatibility
    if (!user.groupId) {
      localStorage.setItem('groupId', user.assignedGroups[0]);
    }
  }

  if (user.assignedHotels && user.assignedHotels.length > 0) {
    localStorage.setItem('assignedHotels', JSON.stringify(user.assignedHotels));
    // Also set hotelId to first hotel for backwards compatibility
    if (!user.hotelId) {
      localStorage.setItem('hotelId', user.assignedHotels[0]);
    }
  }

  // Capabilities (Decision 7): granted independently of the role.
  if (user.capabilities && user.capabilities.length > 0) {
    localStorage.setItem('capabilities', JSON.stringify(user.capabilities));
  } else {
    localStorage.removeItem('capabilities');
  }

  // Guardar expiración si existe
  if (expiresIn) {
    const expiration = Date.now() + (expiresIn * 1000);
    localStorage.setItem('token_expiration', expiration.toString());
  }
};

/**
 * Limpia todos los datos de autenticación
 */
export const clearAuthData = (): void => {
  stopImpersonation();
  localStorage.removeItem('token');
  localStorage.removeItem('token_expiration');
  localStorage.removeItem('userId');
  localStorage.removeItem('username');
  localStorage.removeItem('email');
  localStorage.removeItem('role');
  localStorage.removeItem('hotelId');
  localStorage.removeItem('groupId');
  localStorage.removeItem('assignedGroups');
  localStorage.removeItem('assignedHotels');
  localStorage.removeItem('capabilities');
  localStorage.removeItem('auth_method');
  localStorage.removeItem('identity_provider');
  localStorage.removeItem('documentation_only');
};

/**
 * Capacidades concedidas al usuario con independencia de su rol (Decision 7).
 * Mientras se suplanta identidad sigue las capacidades del usuario suplantado.
 */
export const getCurrentCapabilities = (): string[] => {
  const impersonation = getImpersonation();
  if (impersonation) return [];
  try {
    const raw = localStorage.getItem('capabilities');
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
};

/**
 * Whether the current user should see the Técnico rail group: the
 * 'technical' capability (granted per user, any role) or Admin/SuperAdmin
 * (who have it implicitly per proposal §2).
 */
export const hasTechnicalCapability = (): boolean => {
  const role = getCurrentRole();
  if (role === 'Admin' || role === 'SuperAdmin') return true;
  return getCurrentCapabilities().includes('technical');
};

/**
 * Monitor de errores and Logs: chain managers (GroupAdmin) as well as technical
 * users. The API applies the same rule and scopes the data to the user's hotels.
 */
export const canViewErrorMonitor = (): boolean =>
  hasTechnicalCapability() || getCurrentRole() === 'GroupAdmin';

/**
 * Obtiene todos los datos del usuario actual
 */
export const getCurrentUser = (): Partial<AuthUser> | null => {
  if (!isAuthenticated()) return null;

  const assignedGroups = getAssignedGroups();
  const assignedHotels = getAssignedHotels();

  return {
    id: getCurrentUserId() || undefined,
    name: getCurrentUserName() || undefined,
    email: getCurrentUserEmail() || undefined,
    role: getCurrentRole() || undefined,
    hotelId: getCurrentHotel() || undefined,
    groupId: getCurrentGroup() || undefined,
    assignedGroups: assignedGroups.length > 0 ? assignedGroups : undefined,
    assignedHotels: assignedHotels.length > 0 ? assignedHotels : undefined,
    capabilities: getCurrentCapabilities(),
    token: getToken() || undefined,
  };
};


export    const decodeJWT = (token: string) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join('')
        );
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Error decoding JWT:', error);
        return null;
    }
};


/**
 * Verifica si un token ha expirado
 */
export const isTokenExpired = (token: string): boolean => {
  const decoded = decodeJWT(token);
  
  if (!decoded || !decoded.exp) {
    return true; // Si no podemos decodificar o no tiene exp, consideramos que expiró
  }
  
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
};

/**
 * Obtiene el tiempo restante hasta que expire el token (en segundos)
 */
export const getTokenTimeRemaining = (token: string): number => {
  const decoded = decodeJWT(token);
  
  if (!decoded || !decoded.exp) {
    return 0;
  }
  
  const currentTime = Math.floor(Date.now() / 1000);
  const timeRemaining = decoded.exp - currentTime;
  
  return Math.max(0, timeRemaining);
};

/**
 * Obtiene la fecha de expiración del token
 */
export const getTokenExpirationDate = (token: string): Date | null => {
  const decoded = decodeJWT(token);
  
  if (!decoded || !decoded.exp) {
    return null;
  }
  
  return new Date(decoded.exp * 1000);
};

/**
 * Extrae información adicional del token JWT
 */
export const extractTokenData = (token: string): {
  hotelId?: string;
  groupId?: string;
  hotelName?: string;
  groupName?: string;
  exp?: number;
} | null => {
  const decoded = decodeJWT(token);
  if (!decoded) return null;
  
  return {
    hotelId: decoded.hotelId,
    groupId: decoded.groupId,
    hotelName: decoded.hotelName,
    groupName: decoded.groupName,
    exp: decoded.exp,
  };
};

/**
 * Verifica si el usuario tiene acceso a nivel de hotel
 */
/**
 * Verifica si el usuario actual inició sesión con credenciales locales (usuario y contraseña).
 * Devuelve false si el usuario inició sesión con un proveedor externo (Microsoft, Google, etc.)
 */
export const hasLocalCredentials = (): boolean => {
  return !localStorage.getItem('identity_provider');
};

/**
 * Verifica si el usuario tiene acceso a nivel de hotel
 */
export const hasHotelAccess = (): boolean => {
  const role = getCurrentRole();
  return role === 'HotelAdmin' || role === 'Receptionist';
};

/**
 * Verifica si el usuario tiene acceso a nivel de grupo
 */
export const hasGroupAccess = (): boolean => {
  const role = getCurrentRole();
  return role === 'GroupAdmin';
};

/**
 * Verifica si el usuario tiene acceso a nivel de sistema
 * (Admin mirrors SuperAdmin everywhere except the Backups tab)
 */
export const hasSystemAccess = (): boolean => {
  const role = getCurrentRole();
  return role === 'SuperAdmin' || role === 'Admin';
};


const roleLevels = {
    'SuperAdmin': 0,
    'Admin': 1,
    'GroupAdmin': 2,
    'HotelAdmin': 3,
    'Receptionist': 4,
    'Viewer': 5
};

export const getCurrentLevel = () => {
    const role = getCurrentRole();
    return role ? roleLevels[role] ?? 4 : 4;
};